import { ModelDataType } from 'src/app/interfaces/model-data-type';

import { ApVendor } from './ap-vendor';
import { BaseModel } from './base-model';
import { Company } from './company';

export class ApConfig extends BaseModel {
  static override columnMap = new Map<
    string,
    { property: string; type: 'column'; dataType: ModelDataType }
  >();
  static override associationMap = new Map<
    string,
    { property: string; type: 'association'; model: string }
  >();
  static override aliasedColumnMap = new Map<
    string,
    { property: string; type: 'alias' }
  >();

  static ApConfigProperties: (
    | {
        property: keyof ApConfig;
        type: 'column';
        dataType: ModelDataType;
      }
    | {
        property: keyof ApConfig;
        type: 'alias';
      }
    | {
        property: keyof ApConfig;
        type: 'association';
        model: string;
      }
  )[] = [
    { property: 'id', type: 'column', dataType: 'number' },
    { property: 'CompanyId', type: 'column', dataType: 'number' },
    { property: 'apSystem', type: 'column', dataType: 'string' },
    { property: 'apDisplayName', type: 'column', dataType: 'string' },
    { property: 'apInstance', type: 'column', dataType: 'string' },
    { property: 'endpointUrl', type: 'column', dataType: 'string' },
    { property: 'clientCode', type: 'column', dataType: 'string' },
    { property: 'username', type: 'column', dataType: 'string' },
    { property: 'password', type: 'column', dataType: 'string' },
    { property: 'allowAutolinks', type: 'column', dataType: 'boolean' },
    { property: 'syncInterval', type: 'column', dataType: 'string' },
    { property: 'isTwoWay', type: 'column', dataType: 'boolean' },
    { property: 'allowCreateVendors', type: 'column', dataType: 'boolean' },
    { property: 'allowPullSites', type: 'column', dataType: 'boolean' },
    { property: 'pushesActiveStatus', type: 'column', dataType: 'boolean' },
    { property: 'pushesHold', type: 'column', dataType: 'boolean' },
    { property: 'pushesInsurance', type: 'column', dataType: 'boolean' },
    { property: 'pushExpInsHold', type: 'column', dataType: 'boolean' },
    { property: 'allowVendorNameEdit', type: 'column', dataType: 'boolean' },
    { property: 'allowVendorAddressEdit', type: 'column', dataType: 'boolean' },
    { property: 'allowVendorContactEdit', type: 'column', dataType: 'boolean' },
    {
      property: 'allowVendorServiceTypeEdit',
      type: 'column',
      dataType: 'boolean'
    },
    { property: 'allowVendorEinEdit', type: 'column', dataType: 'boolean' },
    { property: 'allowVendor1099Edit', type: 'column', dataType: 'boolean' },
    { property: 'showStatusChange', type: 'column', dataType: 'boolean' },
    { property: 'showExemptChange', type: 'column', dataType: 'boolean' },
    { property: 'isManualOnlySync', type: 'column', dataType: 'boolean' },
    { property: 'nextVendorSync', type: 'column', dataType: 'date' },
    { property: 'lastVendorSync', type: 'column', dataType: 'date' },
    { property: 'lastVendorSyncStatus', type: 'column', dataType: 'string' },
    { property: 'retryInterval', type: 'column', dataType: 'string' },
    { property: 'createdAt', type: 'column', dataType: 'date' },
    { property: 'updatedAt', type: 'column', dataType: 'date' },
    { property: 'authTokenUrl', type: 'column', dataType: 'string' },
    { property: 'scope', type: 'column', dataType: 'string' },
    { property: 'v3CutOverDate', type: 'column', dataType: 'date' },

    { property: 'integrationType', type: 'alias' },

    { property: 'ApVendors', model: 'ApVendor', type: 'association' },
    { property: 'Company', model: 'Company', type: 'association' }
  ];

  static {
    ApConfig.ApConfigProperties.forEach((propertyObject) => {
      const { property, type } = propertyObject;
      if (type === 'column') {
        ApConfig.columnMap.set(property, propertyObject);
      }
      if (type === 'alias') {
        ApConfig.aliasedColumnMap.set(
          property,
          propertyObject as { property: string; type: 'alias' }
        );
      }
      if (type === 'association') {
        ApConfig.associationMap.set(
          property,
          propertyObject as {
            property: string;
            type: 'association';
            model: string;
          }
        );
      }
    });
  }

  constructor(ApConfigObject: Partial<ApConfig>) {
    super();

    ApConfig.ApConfigProperties.forEach((propertyObject) => {
      const { property } = propertyObject;
      const value = ApConfigObject[property];
      if (value !== undefined) {
        Object.assign(this, { [property]: value });
      }
    });
  }

  id?: number;
  CompanyId?: number;
  apSystem?:
    | 'Jenark'
    | 'Caliber'
    | 'VMS'
    | 'Vantaca'
    | 'Cinc'
    | 'CamAcct'
    | 'eUnify'
    | 'Yardi'
    | 'MRI'
    | 'RentManager'
    | 'AppFolio';
  apDisplayName?: string | null;
  apInstance?: string | null;
  endpointUrl?: string;
  clientCode?: string | null;
  username?: string | null;
  password?: string | null;
  allowAutolinks?: boolean;
  syncInterval?: ('12H' | '24H') | null;
  isTwoWay?: boolean;
  allowCreateVendors?: boolean;
  allowPullSites?: boolean;
  pushesActiveStatus?: boolean;
  pushesHold?: boolean;
  pushesInsurance?: boolean;
  pushExpInsHold?: boolean;
  allowVendorNameEdit?: boolean;
  allowVendorAddressEdit?: boolean;
  allowVendorContactEdit?: boolean;
  allowVendorServiceTypeEdit?: boolean;
  allowVendorEinEdit?: boolean;
  allowVendor1099Edit?: boolean;
  showStatusChange?: boolean;
  showExemptChange?: boolean;
  isManualOnlySync?: boolean;
  nextVendorSync?: Date | null;
  lastVendorSync?: Date | null;
  lastVendorSyncStatus?:
    'CREATED' | 'SUCCESS' | 'AUTH' | 'TIMEOUT' | 'PARSE' | 'MISC';
  retryInterval?: '1H' | '2H' | '4H' | '8H' | '12H' | 'NO';
  createdAt?: Date;
  updatedAt?: Date;
  authTokenUrl?: string | null;
  scope?: string | null;
  v3CutOverDate?: Date | null;

  integrationType?: string;

  ApVendors?: ApVendor[];
  Company?: Company;
}
